<script lang="ts">
  import { onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';

  import { Button, InlineNotification, NotificationActionButton } from 'carbon-components-svelte';
  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import DeleteSlaTaskModal from './DeleteSlaTaskModal.svelte';
  import LoadingButton from '../../../components/LoadingButton.svelte';
  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import SlaTaskForm from './SlaTaskForm.svelte';
  import ToggleSlaTaskModal from './ToggleSlaTaskModal.svelte';

  import { SlaTaskFormVariants } from './sla-task-form-shared';

  import { getSLATask } from '../../../services';
  import { cacheEntityName } from '../../../stores';

  import type { SlaTaskRow } from '../../../../shared/types/sla-task-types';

  type SlaTaskModalAction = 'delete-task' | 'disable-task' | 'enable-task';
  type SlaTaskModalCloseDetail = { action: SlaTaskModalAction; data: { id: string; friendlyName: string } };

  export let slaTaskId: string;

  const navigate = useNavigate();

  let notification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null,
    loading = false,
    slaTask: SlaTaskRow | null = null,
    formSubmitButtonRef: HTMLElement | undefined,
    modals = {
      deleteSlaTask: { open: false },
      toggleSlaTask: { open: false },
    };

  async function loadSlaTaskData(
    successNotification?: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>>
  ): Promise<void> {
    notification = successNotification ?? null;
    loading = true;

    try {
      const task = await getSLATask(slaTaskId).then(({ schedule, ...rest }) => ({
        ...rest,
        time: schedule?.time ?? rest.time,
        timezone: schedule?.timezone ?? rest.timezone,
      }));
      slaTask = task;
      loading = false;
      cacheEntityName(slaTaskId, task.friendlyName);
    } catch (error) {
      console.error('[SlaBasicInformation] Failed to retrieve SLA task data!', error);
      loading = false;
      notification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to retrieve SLA task data. Please try again later.',
      };
    }
  }

  function submitForm() {
    if (!formSubmitButtonRef) {
      throw new Error('Form submit button not found!');
    }

    formSubmitButtonRef.click();
  }

  function onFormSubmit() {
    loadSlaTaskData({
      kind: 'success',
      title: 'Success:',
      subtitle: 'SLA task updated successfully.',
    });
  }

  function dispatchHandler(eventName: SlaTaskModalAction) {
    switch (eventName) {
      case 'disable-task':
      case 'enable-task':
        onFormSubmit();
        break;
      case 'delete-task':
        navigate('/data-feeds#sla');
        break;
      default:
        console.warn(`Unhandled SLA task modal action: ${eventName}`);
    }
  }

  function openModal(name: keyof typeof modals) {
    return function handleOpen() {
      modals = {
        ...modals,
        [name]: { open: true },
      };
    };
  }

  function closeModal(name: keyof typeof modals) {
    return function handleClose(event: CustomEvent<SlaTaskModalCloseDetail | undefined>): void {
      const { detail } = event;

      if (detail?.action) {
        dispatchHandler(detail.action);
      }

      modals = {
        ...modals,
        [name]: { open: false },
      };
    };
  }

  onMount(loadSlaTaskData);
</script>

<LoadingSpinner {loading}>
  {#if notification}
    <InlineNotification
      lowContrast
      kind={notification.kind}
      title={notification.title}
      subtitle={notification.subtitle}
      on:close={() => (notification = null)}
    />
  {/if}

  {#if slaTask}
    {#if !slaTask.active}
      <InlineNotification
        hideCloseButton
        lowContrast
        kind="warning"
        subtitle="This SLA task is currently disabled and will not trigger any alerts."
      >
        <svelte:fragment slot="actions">
          <NotificationActionButton on:click={openModal('toggleSlaTask')}>Enable</NotificationActionButton>
        </svelte:fragment>
      </InlineNotification>
    {/if}

    <section class="section">
      <SlaTaskForm
        variant={SlaTaskFormVariants.EDIT_ALL}
        bind:formSubmitButtonRef
        bind:loading
        initialValues={slaTask}
        on:submit={onFormSubmit}
      />

      <div class="form-controls">
        <Button kind="danger-tertiary" disabled={loading} on:click={openModal('deleteSlaTask')}>Delete SLA Task</Button>
        <Button kind="tertiary" disabled={loading} on:click={openModal('toggleSlaTask')}>
          {slaTask?.active === false ? 'Enable' : 'Disable'} SLA Task
        </Button>
        <LoadingButton kind="primary" on:click={submitForm} disabled={loading} isLoading={loading}>Save</LoadingButton>
      </div>
    </section>

    {#if modals.deleteSlaTask.open}
      <DeleteSlaTaskModal open slaTaskId={slaTask?.id} slaTaskName={slaTask?.friendlyName ?? ''} on:close={closeModal('deleteSlaTask')} />
    {/if}

    {#if modals.toggleSlaTask.open}
      <ToggleSlaTaskModal
        open
        slaTaskId={slaTask.id}
        slaTaskName={slaTask.friendlyName}
        active={slaTask.active !== false}
        on:close={closeModal('toggleSlaTask')}
      />
    {/if}
  {/if}
</LoadingSpinner>

<style>
  .section {
    margin-bottom: 2rem;
  }

  .form-controls {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
  }
</style>
